import type * as T from "@shared/types";

const PBB_SELLING_PLATFORM = "PBB";

const US_CHANNEL = {
  BEANZ: "BUSMP",
  PBB: "PBBUS",
} as const;

const normalize = (value: string): string => value.trim().toUpperCase();

/**
 * A vendor that exclusively sells SKUs through the PBB partner site uses
 * the `PBBUS` channel, while a normal Beanz vendor uses the `BUSMP` channel.
 *
 * The `sellingPlatforms` configuration is maintained at the vendor level
 * and is received from Salesforce. This configuration is optional and is
 * currently populated only for PBB vendors.
 *
 * Therefore:
 * - PBB vendor -> PBBUS
 * - Normal vendor / no PBB configuration -> BUSMP
 */
export const isPbbVendor = (vendor: T.Vendor): boolean =>
  vendor.properties?.sellingPlatforms?.some((platform) => normalize(platform) === PBB_SELLING_PLATFORM) ?? false;

const getVendorUsChannel = (vendor: T.Vendor): string => (isPbbVendor(vendor) ? US_CHANNEL.PBB : US_CHANNEL.BEANZ);

/**
 * PIM can return both `BUSMP` and `PBBUS` for the same SKU because both
 * channels resolve to the US ship-to country.
 *
 * For the current vendor, keep only the US channel that matches their
 * selling platform. Non-US channels such as `BNLMP`, `BDEMP`, etc. are
 * always retained.
 */
const filterVariantShipToInfo = (variant: T.Variant, vendorUsChannel: string): T.Variant => {
  if (!variant.shipToInfo) {
    return variant;
  }
  return {
    ...variant,
    shipToInfo: variant.shipToInfo.filter(({ channel }) => {
      const normalizedChannel = normalize(channel);
      if (normalizedChannel === US_CHANNEL.BEANZ || normalizedChannel === US_CHANNEL.PBB) {
        return normalizedChannel === vendorUsChannel;
      }
      return true;
    }),
  };
};

export const selectSellingPlatformChannels = (products: T.Product[], vendor: T.Vendor): T.Product[] => {
  const vendorUsChannel = getVendorUsChannel(vendor);
  return products.map((product) => ({
    ...product,
    variants: product.variants?.map((variant) => filterVariantShipToInfo(variant, vendorUsChannel)),
  }));
};
